import React, { useEffect } from "react";
import { Button, Spinner } from "reactstrap";
import {
	estimateSchemaRunSeconds,
	formatRunDuration,
} from "./schemaOptimizerRunEstimate";

/**
 * Full-bleed confirmation step inside the generate modal (avoids nested modals/backdrops).
 */
export default function SchemaOptimizerRunConfirmModal({
	isOpen,
	urlCount,
	useBatchApi,
	truncated = false,
	totalFound = 0,
	pending = false,
	onCancel,
	onConfirm,
}) {
	useEffect(() => {
		if (!isOpen) return undefined;
		const onKey = (event) => {
			if (event.key === "Escape" && !pending) onCancel();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [isOpen, onCancel, pending]);

	if (!isOpen) return null;

	const pagesLabel = `${urlCount.toLocaleString()} page${urlCount === 1 ? "" : "s"}`;
	const duration = formatRunDuration(estimateSchemaRunSeconds(urlCount, useBatchApi));
	const processingLabel = useBatchApi ? "OpenAI batch" : "Real-time";

	return (
		<div
			className="schema-optimizer__run-confirm-step"
			role="alertdialog"
			aria-modal="true"
			aria-labelledby="schema-run-confirm-title"
			aria-describedby="schema-run-confirm-description"
		>
			<div className="schema-optimizer__run-confirm-step-card">
				<div className="schema-optimizer__run-confirm-step-header">
					<div className="schema-optimizer__run-confirm-step-icon" aria-hidden>
						<i className="fas fa-layer-group" />
					</div>
					<div>
						<h2 id="schema-run-confirm-title" className="schema-optimizer__run-confirm-step-title">
							Confirm bulk run
						</h2>
						<p className="schema-optimizer__run-confirm-step-subtitle mb-0">
							Review scope and timing before schema optimization starts.
						</p>
					</div>
				</div>

				<div className="schema-optimizer__run-confirm-stats" id="schema-run-confirm-description">
					<div className="schema-optimizer__run-confirm-stat">
						<span className="schema-optimizer__run-confirm-stat-label">Pages in this run</span>
						<span className="schema-optimizer__run-confirm-stat-value">{urlCount.toLocaleString()}</span>
					</div>
					<div className="schema-optimizer__run-confirm-stat">
						<span className="schema-optimizer__run-confirm-stat-label">Estimated time</span>
						<span className="schema-optimizer__run-confirm-stat-value">{duration}</span>
					</div>
					<div className="schema-optimizer__run-confirm-stat">
						<span className="schema-optimizer__run-confirm-stat-label">Processing</span>
						<span className="schema-optimizer__run-confirm-stat-value">{processingLabel}</span>
					</div>
				</div>

				{truncated && totalFound > urlCount ? (
					<div className="schema-optimizer__run-confirm-callout schema-optimizer__run-confirm-callout--info">
						<i className="fas fa-info-circle" aria-hidden />
						<p className="mb-0">
							Your source listed{" "}
							<strong>{totalFound.toLocaleString()} URLs</strong>. Only the first{" "}
							<strong>{urlCount.toLocaleString()}</strong> will run (product limit).
						</p>
					</div>
				) : null}

				<div className="schema-optimizer__run-confirm-callout schema-optimizer__run-confirm-callout--warning">
					<i className="fas fa-clock" aria-hidden />
					<p className="mb-0">
						You are about to validate and optimize JSON-LD for{" "}
						<strong>{pagesLabel}</strong>. You can track progress on the run page after
						starting.
					</p>
				</div>

				<p className="schema-optimizer__run-confirm-detail mb-0">
					{useBatchApi
						? "Pages are fetched in sequence, then processed in one OpenAI batch run (~50% lower cost; best for sitemap and CSV)."
						: "Pages run in parallel (about four at a time). Faster while you wait; standard API pricing."}
				</p>

				<div className="schema-optimizer__run-confirm-actions">
					<Button color="secondary" outline onClick={onCancel} disabled={pending}>
						Back to list
					</Button>
					<Button color="primary" onClick={onConfirm} disabled={pending || urlCount < 1}>
						{pending ? (
							<>
								<Spinner size="sm" className="me-2" />
								Starting…
							</>
						) : (
							<>Start run · {urlCount.toLocaleString()} pages</>
						)}
					</Button>
				</div>
			</div>
		</div>
	);
}
